import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import SendMoneyIcon from 'components/atoms/SVG/SendMoneyIcon.component';
import PaymentAmount from 'components/molecules/Fields/PaymentAmount/PaymentAmount.component';
import ModalHeading from 'components/molecules/ModalHeading';
import { joiResolver } from 'customJoi';
import useInvalidateTransferDependencies from 'hooks/Bank/useInvalidateTransferDependencies';
import useMakeTransfer from 'hooks/Bank/useMakeTransfer';
import Joi from 'joi';
import { motion } from 'motion/react';
import type { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CurrenciesEnum } from 'types/global.types';
import type { Connector, Transaction } from 'types/transactions.types';
import { StandingOrderFrequency } from 'types/transactions.types';
import { WEBAPP_BANKING_PAYMENTTRANSFER_INTERNALTRANSFER_CLICKEDCONFIRMTRANSFER } from 'utils/amplitude/events/bank.amplitude';
import { fadeTransition } from 'utils/constants';
import formatMoney from 'utils/formatMoney';
import {
  TransferFieldNames,
  TransferReasons,
  TransferTypes,
} from '../../helpers';
import SimilarTransactionAlert from '../../SimilarTransactionAlert.component';
import useWalletTopUpBankAccount from '../../useWalletTopUpBankAccount';
import type { TransferReviewData } from '../types';
import { transformFormDataToReviewData } from '../utils/dataTransforms';

interface InternalTransferFormProps {
  sourceAccount: Connector;
  destinationAccount: Connector;
  initialAmount?: number;
  onTransferSuccess?: (transaction?: Transaction) => void;
  onReview?: (data: TransferReviewData) => void;
  showReviewStep?: boolean;
}

const InternalTransferComponent: FC<InternalTransferFormProps> = ({
  sourceAccount,
  destinationAccount,
  initialAmount,
  onTransferSuccess,
  onReview,
  showReviewStep = true,
}) => {
  const { t } = useTranslation();
  const { walletTopUpBankAccountContent, openWalletTopUpBankAccountModal } =
    useWalletTopUpBankAccount();

  const invalidateTransferDeps = useInvalidateTransferDependencies();

  const { mutate, isLoading } = useMakeTransfer({
    onTransferSuccess: () => {
      onTransferSuccess?.();
      invalidateTransferDeps();
    },
  });

  const formAPI = useForm({
    defaultValues: { [TransferFieldNames.amount]: initialAmount || 0 },
    resolver: joiResolver(
      Joi.object({
        [TransferFieldNames.amount]: Joi.number()
          .custom((value, helpers) => {
            const isValid =
              value > 0 && value <= (sourceAccount?.balance.amount || 0);

            if (!isValid) {
              return helpers.error('any.invalid');
            }

            return value;
          })
          .required(),
      }),
    ),
  });

  const transferAmountValue = formAPI.watch(TransferFieldNames.amount);

  const amountExceedsSourceAccountBalance =
    transferAmountValue > (sourceAccount?.balance.amount || 0);

  const handleInternalTransferSubmit = () => {
    if (!sourceAccount || !destinationAccount) return;

    // If we should show review step, prepare review data and call onReview
    if (showReviewStep && onReview) {
      // Create form data object for internal transfer
      const formData = {
        amount: transferAmountValue,
        period: StandingOrderFrequency.OneTime,
        firstExecutionDate: new Date(),
        toConnectorId: destinationAccount._id, // Include destination account ID
        // Let the transfer type be determined by the actual form data
        // Don't hardcode instant or communication_type
      };

      // Use the transformFormDataToReviewData function to properly create review data
      const reviewData = transformFormDataToReviewData(
        formData,
        mutate,
        isLoading,
        sourceAccount._id,
      );

      onReview(reviewData);
    } else {
      // Direct submission (for backward compatibility)
      mutate({
        fromConnectorId: sourceAccount._id,
        toConnectorId: destinationAccount._id,
        amount: { value: transferAmountValue, currency: 'EUR' },
        reason: TransferReasons.transfer,
        type: TransferTypes.internal,
      });
    }
  };

  return (
    <motion.div
      {...fadeTransition}
      key="internal-review"
      className="flex items-center flex-col w-full px-6"
    >
      <ModalHeading
        Icon={SendMoneyIcon}
        title={t('accountable_banking.internal_transfer.title')}
        subtitle={t('accountable_banking.internal_transfer.subtitle')}
      />

      {walletTopUpBankAccountContent}

      <motion.div {...fadeTransition} className="w-full my-6">
        <FormProvider {...formAPI}>
          <form
            className="mb-6"
            onSubmit={formAPI.handleSubmit(handleInternalTransferSubmit)}
          >
            <FieldConnector name={TransferFieldNames.amount} forceHideError>
              {({ field }) => (
                <PaymentAmount
                  {...field}
                  className={classNames(
                    '!text-4xl !font-bold !text-primary !border-0 !text-center',
                    {
                      '!text-red-600':
                        amountExceedsSourceAccountBalance ||
                        (transferAmountValue === 0 &&
                          formAPI.formState.touchedFields[
                            TransferFieldNames.amount
                          ]),
                    },
                  )}
                  placeholder={t('amountSign')}
                />
              )}
            </FieldConnector>

            <p className="text-center text-primary-700 mt-3 mb-6">
              {t('accountable_banking.internal_transfer.available_amount', {
                bankName: sourceAccount?.title,
                amount: formatMoney(
                  sourceAccount?.balance?.amount as number,
                  sourceAccount?.balance?.currency,
                ),
              })}
            </p>

            {amountExceedsSourceAccountBalance ? (
              <>
                <Button
                  className="mt-6 w-full"
                  onClick={() =>
                    openWalletTopUpBankAccountModal({
                      connector: sourceAccount,
                    })
                  }
                >
                  {t('accountable_banking.top_up.cta', {
                    bankAccountName: sourceAccount?.title,
                  })}
                </Button>

                {(sourceAccount?.balance.amount || 0) > 0 && (
                  <Button
                    className="mt-3 w-full"
                    structure="secondary"
                    onClick={() => {
                      formAPI.setValue(
                        TransferFieldNames.amount,
                        sourceAccount?.balance.amount || 0,
                      );
                    }}
                  >
                    {t('accountable_banking.set_aside.cta')}{' '}
                    {formatMoney(sourceAccount?.balance.amount || 0)}
                  </Button>
                )}
              </>
            ) : (
              <div className="space-y-4">
                <SimilarTransactionAlert
                  amount={transferAmountValue}
                  currency={CurrenciesEnum.EUR}
                  toIBAN={destinationAccount?.bankAccountReference?.IBAN || ''}
                  fromIBAN={sourceAccount?.bankAccountReference?.IBAN || ''}
                  onConfirm={handleInternalTransferSubmit}
                  isLoading={isLoading}
                />

                <Button
                  onClick={handleInternalTransferSubmit}
                  loading={isLoading}
                  className="w-full"
                  disabled={!transferAmountValue || transferAmountValue <= 0}
                  tracingEvent={
                    WEBAPP_BANKING_PAYMENTTRANSFER_INTERNALTRANSFER_CLICKEDCONFIRMTRANSFER
                  }
                >
                  {showReviewStep
                    ? t('accountable_banking.review_transfer.title')
                    : t('confirm')}
                </Button>
              </div>
            )}
          </form>
        </FormProvider>
      </motion.div>
    </motion.div>
  );
};

export default InternalTransferComponent;
